/**
 * `feature-preview-program` PRD — the closed catalog of feature previews.
 *
 * A feature preview is an unfinished interface change that one person may
 * turn on or off for themselves (`feature-preview-program:preview-opt-ins-stay-client-local`). New design defaults on when this
 * browser has no valid saved preference (`feature-preview-program:new-design-defaults-on`). It is not a
 * development variant (development-only, `development-variants/`) and not a
 * server feature flag (`/v1/me/context` `features`, per organization).
 *
 * Extending: add the id to `FeaturePreviewId` and an entry below. Retiring:
 * set `status: "retired"` — the store's decoder then drops the id, which turns
 * the preview off everywhere without touching stored state (`feature-preview-program:closed-catalog-drops-retired-previews`). Delete the
 * entry only after the code that reads the id is gone.
 */
export const FeaturePreviewId = {
	OpenConversationTabs: 'open-conversation-tabs',
	/**
	 * `feature-preview-program:merge-redesign-into-single-preview`: the whole new design behind one switch — the reorganized
	 * navigation (Crew, Apps & Skills, Library), the Project rail, the centred new
	 * chat, the Warm theme (brand titles, warm greys, tonal steps, token marks)
	 * and the light/dark theme choice. Code that asks "is the Project rail on?"
	 * reads this id; there are no separate previews for the parts.
	 */
	WorkspaceRedesign: 'workspace-redesign',
} as const;

export type FeaturePreviewIdValue = (typeof FeaturePreviewId)[keyof typeof FeaturePreviewId];

export const FeaturePreviewStatus = {
	/** Listed in the Feature previews dialog and honoured by the store. */
	Available: 'available',
	/** Hidden and decoded as off; kept only so stored opt-ins are dropped deliberately. */
	Retired: 'retired',
} as const;

export type FeaturePreviewStatusValue = (typeof FeaturePreviewStatus)[keyof typeof FeaturePreviewStatus];

export type FeaturePreviewDefinition = {
	readonly id: FeaturePreviewIdValue;
	readonly status: FeaturePreviewStatusValue;
	readonly title: string;
	/** One or two sentences: what changes on screen when the preview is on. */
	readonly description: string;
};

export const FEATURE_PREVIEW_CATALOG: readonly FeaturePreviewDefinition[] = [
	{
		id: FeaturePreviewId.OpenConversationTabs,
		// Disabled 2026-09-30 while the Project rail is evaluated (`feature-preview-program:retire-open-conversation-tabs`).
		// Set back to Available to offer it again; the code is unchanged.
		status: FeaturePreviewStatus.Retired,
		title: 'Open conversation tabs',
		description:
			'Keeps the conversations you are working on as tabs above the page, across projects, showing which are running, which need your input, and which finished since you last looked. The sidebar becomes your conversation history.',
	},
	{
		id: FeaturePreviewId.WorkspaceRedesign,
		status: FeaturePreviewStatus.Available,
		title: 'New design',
		description:
			'A calmer, warmer Crewstack: Crew, Apps & Skills and Library in the sidebar, a column of your projects beside it, a new chat that starts in the middle of the page, and a light or dark theme of your choice.',
	},
];

const KNOWN_AVAILABLE_FEATURE_PREVIEW_IDS: ReadonlySet<string> = new Set(
	FEATURE_PREVIEW_CATALOG.filter((entry) => entry.status === FeaturePreviewStatus.Available).map((entry) => entry.id),
);

/** Narrows untyped input (storage, a link) to an id that is listed and available. */
export function isAvailableFeaturePreviewId(value: unknown): value is FeaturePreviewIdValue {
	return typeof value === 'string' && KNOWN_AVAILABLE_FEATURE_PREVIEW_IDS.has(value);
}

export function listAvailableFeaturePreviews(): readonly FeaturePreviewDefinition[] {
	return FEATURE_PREVIEW_CATALOG.filter((entry) => entry.status === FeaturePreviewStatus.Available);
}
